import { CalendarMonth, InfoOutlined, WarningAmber } from '@mui/icons-material';
import { Alert, Box, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { GridContainer, GridItem } from '../../../../components/shared';
import { formatCurrencyValue } from '../../../../utils/common-methods';

const MonthlyReconciliationSummary = ({
  appliedFilters,
  classes,
  feedsError,
  finalizedWindowAlert,
  hasExceptions,
  isSummaryLoading,
  reconciliationData,
  reconciliationSummary,
}) => {
  if (appliedFilters && isSummaryLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
        <CircularProgress />
      </Box>
    );
  }

  if (appliedFilters && !isSummaryLoading && feedsError) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {feedsError}
      </Alert>
    );
  }

  if (!appliedFilters) {
    return (
      <Box className={classes.emptyState}>
        <CalendarMonth className={classes.emptyStateIcon} />
        <Typography variant="body1" color="text.secondary">
          Select X3 Product Name, Start Date and End Date (MM/DD/YYYY), then click Apply to view reconciliation data.
        </Typography>
      </Box>
    );
  }

  if (!reconciliationSummary) return null;

  return (
    <>
      {finalizedWindowAlert ? (
        <Alert severity={finalizedWindowAlert.severity} sx={{ mb: 2 }}>
          {finalizedWindowAlert.message}
        </Alert>
      ) : null}

      <GridContainer spacing={3} mb={3} sx={{ pl: 1.5 }}>
        <GridItem xs={6} sm={3} md={2}>
          <Box className={classes.statLabelContainer}>
            <Typography variant="caption" color="secondary.main" className={classes.statLabel}>
              Total Asset
            </Typography>
          </Box>
          <Typography variant="h3" className={classes.statValue}>
            {reconciliationData?.totalAssets || 0}
          </Typography>
        </GridItem>

        <GridItem xs={6} sm={3} md={3}>
          <Box className={classes.statLabelContainer}>
            <Typography variant="caption" color="secondary.main" className={classes.statLabel}>
              Assets with Variance
            </Typography>
            <Tooltip
              title="Number of assets with a non-zero variance between current and posted amounts for the selected monthly reconciliation range."
              arrow
            >
              <InfoOutlined className={classes.infoIcon} />
            </Tooltip>
          </Box>
          <Typography variant="h3" className={classes.statValue}>
            {reconciliationData?.assetsWithVariance ?? 0}
          </Typography>
        </GridItem>
      </GridContainer>

      {hasExceptions ? (
        <GridContainer spacing={2} sx={{ mb: 3 }}>
          <GridItem xs={12} md={6}>
            <Paper elevation={0} className={`${classes.warningBox} ${classes.missingDataFeedBox}`}>
              <WarningAmber className={classes.warningBoxIcon} />
              <Box>
                <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                  <Typography variant="body1" className={classes.warningBoxText}>
                    Missing Data Feed Count: {reconciliationSummary?.missingDataFeedCount ?? 0}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </GridItem>

          <GridItem xs={12} md={6}>
            <Paper elevation={0} className={`${classes.warningBox} ${classes.unmappedDataFeedBox}`}>
              <InfoOutlined className={classes.errorBoxIcon} />
              <Box>
                <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                  <Typography variant="body1" className={classes.unmappedDataFeedText}>
                    Total Unmapped Data Feed:{' '}
                    {formatCurrencyValue(reconciliationSummary?.totalUnmappedDataFeed ?? 0, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </GridItem>
        </GridContainer>
      ) : null}
    </>
  );
};

export default MonthlyReconciliationSummary;
